import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../icon';
import getLabelStyles from './helpers/get-label-styles';
import { APPEARANCES, LABEL_POSITIONS, LABEL_GAPS, VARIABLES, COLORS } from './constants';

function Highlighter({
  elementBounds,
  withNegativeIndent = false,
  visible,
  hovered = false,
  iconGlyph = '',
  label = '',
  appearance = APPEARANCES.default,
  color = COLORS.default,
  labelPosition = LABEL_POSITIONS.leftBottom,
  labelGap = LABEL_GAPS.small,
  fullwidthLabel = false,
  clipPath = null,
}) {
  const { width, height, left, top } = elementBounds;
  const indent = withNegativeIndent ? `${VARIABLES.borderIndent} * -1` : VARIABLES.borderIndent;
  const highlighterWidth = `calc(${Math.round(width)}px + ${indent} * 2)`;
  const highlighterHeight = `calc(${Math.round(height)}px + ${indent} * 2)`;
  const offsetX = `calc(${Math.round(left)}px - ${indent} - ${VARIABLES.borderWidth})`;
  const offsetY = `calc(${Math.round(top)}px - ${indent} - ${VARIABLES.borderWidth})`;

  const highlighterStyle = {
    bottom: `${-top}px`,
    height: `calc(${top + height}px + ${indent} + ${VARIABLES.borderWidth})`,
  };
  const highlighterMaskStyle = {
    width: highlighterWidth,
    height: highlighterHeight,
    left: offsetX,
    top: offsetY,
    ...(clipPath && { clipPath }),
  };
  const labelStyles = getLabelStyles(elementBounds, indent, labelPosition, fullwidthLabel);

  const highlighterClassName = classNames('ws-ui-highlighter', `ws-ui-highlighter--${appearance}`, {
    'ws-ui-highlighter--visible': visible,
    'ws-ui-highlighter--hovered': hovered,
    [`ws-ui-highlighter--${color}`]: color !== COLORS.default,
  });
  const labelClassName = classNames(
    'ws-ui-highlighter__label',
    `ws-ui-highlighter__label--gap-${labelGap}`,
    `ws-ui-highlighter__label--${labelPosition}`,
    {
      'ws-ui-highlighter__label--hovered': hovered,
    }
  );

  return (
    <div className={highlighterClassName} style={highlighterStyle}>
      <div className="ws-ui-highlighter__mask" style={highlighterMaskStyle} />
      {label && (
        <div className={labelClassName} style={labelStyles}>
          {iconGlyph && <Icon className="ws-ui-highlighter__label-icon" glyph={iconGlyph} />}
          <span className="ws-ui-highlighter__label-description">{label}</span>
        </div>
      )}
    </div>
  );
}

Highlighter.propTypes = {
  elementBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    left: PropTypes.number,
    top: PropTypes.number,
  }).isRequired,
  visible: PropTypes.bool.isRequired,
  withNegativeIndent: PropTypes.bool,
  hovered: PropTypes.bool,
  appearance: PropTypes.string,
  color: PropTypes.oneOf(Object.values(COLORS)),
  iconGlyph: PropTypes.string,
  label: PropTypes.string,
  labelPosition: PropTypes.string,
  labelGap: PropTypes.string,
  fullwidthLabel: PropTypes.bool,
  clipPath: PropTypes.string,
};

export default Highlighter;
